import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';

import './index.module.less';

import { animationDurations, rem } from '@helpers/Constants';

export function getPageWidth() {
  return document.documentElement.clientWidth;
}

export function getPageHeight() {
  return document.documentElement.clientHeight;
}

// 获取元素的布局信息
export async function getElementRect(element: HTMLDivElement ) {
  const rect = element.getBoundingClientRect();
  return rect;
}

export interface IPopupMenuItemProps<T> {
  title: string;
  icon?: string;
  value?: T;
}
export class PopupMenuItem<T> {
  title: string;
  icon?: string;
  value?: T;

  constructor(props: IPopupMenuItemProps<T>) {
    this.title = props.title;
    this.icon = props.icon;
    this.value = props.value;
  }
}

interface PopupMenuProps<T> {
  width: number;
  items: PopupMenuItem<T>[];
  initialIndex?: number;
  outsideDismiss?: boolean;
  anchor: React.RefObject<HTMLDivElement>;
  anchorMargin?: number;
  anchorRect?: any;
  onTap?: (index: number, value?: T) => void;
}

export class PopupMenu<T> extends React.Component<PopupMenuProps<T>> {
  defaultAnchorMargin = 10;

  prompt: React.RefObject<HTMLDivElement> = React.createRef();

  close = () => {
    const confirm = this.prompt.current;
    if (confirm) {
      const className = confirm.className.replace('in-down', 'out-up');
      confirm.className = className;
      // 小程序端无法正常触发animationend，此处采用计时器模拟
      if (process.env.isMiniprogram) {
        setTimeout(this.animationEnd, animationDurations.fadeOutUp);
      }
    }
  };

  animationEnd = (event?: React.AnimationEvent): void => {
    if (!event || event.animationName === 'fadeOutUp') {
      ReactDOM.unmountComponentAtNode(
        document.getElementById('prompt') as HTMLDivElement
      );
    }
  };

  render(): React.ReactNode {
    const {
      width,
      items,
      anchorMargin,
      anchorRect,
      outsideDismiss = true
    } = this.props;

    const pageWidth = getPageWidth();
    const containerRight = Math.max(pageWidth - (anchorRect?.right || 0), 0);
    const containerTop = Math.max(
      (anchorRect?.top || 0) + (anchorRect?.height || 0),
      0
    );
    const containerStyle: React.CSSProperties = {
      width: width * 0.02 * rem,
      right: containerRight,
      top: containerTop + ((anchorMargin || this.defaultAnchorMargin) * 0.02 * rem)
    };

    const popupMenuList = items.map((item: PopupMenuItem<T>, index: number) => {
      const { initialIndex = -1, onTap } = this.props;
      return (
        <div
          styleName={ `popup-menu-item${
            initialIndex === index ? ' selected' : ''
          }` }
          key={ item.title }
          onClick={ () => {
            onTap && onTap(index, item.value);
          } }
        >
          <i
            styleName={ `icon${ item.icon === undefined ? ' none' : '' }` }
            className={ 'iconfont ' + item.icon }
          />
          <div>{item.title}</div>
        </div>
      );
    });

    return (
      <div
        className="bg-modal"
        onClick={ () => {
          if (outsideDismiss) {
            this.close();
          }
        } }
      >
        <div
          className="flex modal-shadow fade-in-down"
          styleName="container"
          style={ containerStyle }
          onAnimationEnd={ this.animationEnd }
          ref={ this.prompt }
        >
          <div styleName="popup-menu-list">{popupMenuList}</div>
        </div>
      </div>
    );
  }

  renderPopupMenuItem() {
    return <div />;
  }
}

interface ExportedPopupMenuProps<T = any> {
  show(props: PopupMenuProps<T>): void;
}

const ExportedPopupMenu: ExportedPopupMenuProps = {
  async show(options) {
    const rect = options.anchorRect || await getElementRect(options.anchor.current);
    const props = {
      ...options,
      anchorRect: rect
    };
    ReactDOM.render(
      <PopupMenu { ...props } />,
      document.getElementById('prompt')
    );
  }
};

export default ExportedPopupMenu;
